// Central SEO configuration. Canonical/OG use the production domain.
export const SITE_URL = "https://airotech.io";
export const SITE_NAME = "AIROTECH";
export const DEFAULT_TITLE = "AIROTECH — AI Transformation & Custom Software Development Studio";
export const DEFAULT_DESCRIPTION =
  "AIROTECH is a digital product studio building AI transformation, custom software, web, mobile and SaaS products — from idea to launch. Start your build today.";
export const DEFAULT_OG = `${SITE_URL}/hero-devices.jpg`;

export const abs = (path = "/") => `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AIROTECH Inc.",
  alternateName: "AIROTECH",
  url: SITE_URL,
  logo: `${SITE_URL}/airotech-mark.png`,
  description: DEFAULT_DESCRIPTION,
  areaServed: ["US", "Worldwide"],
  address: {
    "@type": "PostalAddress",
    addressRegion: "GA",
    addressLocality: "Atlanta",
    addressCountry: "US",
  },
  sameAs: [],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
});

export const serviceSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: page.h1,
  name: page.metaTitle,
  description: page.metaDescription,
  url: abs(`/${page.slug}`),
  areaServed: ["Atlanta", "Georgia", "United States", "Worldwide"],
  provider: {
    "@type": "Organization",
    name: "AIROTECH Inc.",
    url: SITE_URL,
  },
});

export const faqSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: abs(it.to),
  })),
});

export const articleSchema = (a) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: a.title,
  description: a.description,
  url: abs(`/insights/${a.slug}`),
  mainEntityOfPage: abs(`/insights/${a.slug}`),
  datePublished: a.publishDate,
  dateModified: a.updatedDate || a.publishDate,
  articleSection: a.category,
  keywords: (a.topics || []).join(", "),
  image: DEFAULT_OG,
  author: { "@type": "Organization", name: "AIROTECH Inc.", url: SITE_URL },
  publisher: {
    "@type": "Organization",
    name: "AIROTECH Inc.",
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/airotech-mark.png` },
  },
});

// SEO metadata for the primary static marketing pages.
export const STATIC_META = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  "/start": {
    title: "Start Your Build — Turn Your Idea Into a Product | AIROTECH",
    description: "Start your build with AIROTECH. Tell us about your idea and we'll turn it into a launch-ready web, mobile, SaaS or AI product. Confidential intake, guided from idea to launch.",
  },
  "/what-we-build": {
    title: "What We Build — Web, Mobile, SaaS & AI Products | AIROTECH",
    description: "Websites, web apps, mobile apps, PWAs, SaaS platforms, dashboards, AI products and custom software — built to ship. See what AIROTECH builds.",
  },
  "/services": {
    title: "Services, Add-ons & Offerings | AIROTECH",
    description: "Explore AIROTECH's services, add-ons and ongoing offerings — from business formation and strategy to implementation and fractional support.",
  },
  "/how-it-works": {
    title: "How It Works — From Idea to Launch | AIROTECH",
    description: "A guided journey from idea to launch: intake, strategy, proposal, build, launch and growth. See how AIROTECH turns your idea into a product.",
  },
  "/build-levels": {
    title: "Build Levels — Which Version of Your Idea? | AIROTECH",
    description: "From simple sites to full multi-user products, AIROTECH helps you choose the right build level for your idea during a guided assessment.",
  },
  "/our-story": {
    title: "Our Story | AIROTECH",
    description: "AIROTECH is a digital product studio helping founders and businesses turn ideas into real, launch-ready software.",
  },
  "/why": {
    title: "Why AIROTECH | AI-Native Product Studio",
    description: "Modern AI makes building more accessible. Experience makes building it right possible. Why teams choose AIROTECH to build their product.",
  },
  "/insights": {
    title: "Insights & Guides — AI, Software & Product Strategy | AIROTECH",
    description: "Practical, original guides on AI transformation, custom software, app development and product strategy from the team at AIROTECH. No hype, just what works.",
  },
};
